import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * Custom PWA install banner.
 *
 * Listens for the `beforeinstallprompt` event (Android Chromium, Edge,
 * desktop Chrome). When supported AND the app is not already installed
 * AND the user has not dismissed the banner before, displays a small
 * bottom-right card with an "Installer" CTA.
 *
 * For iOS Safari (no native prompt API) we surface a short instruction
 * banner the first time the user visits, then never again.
 *
 * Variants:
 *  - variant="floating"  : fixed bottom-right card (Landing default)
 *  - variant="inline"    : full-width card inside the page (Dashboard)
 */
export default function PWAInstallBanner({ variant = 'floating', storageKey = 'av-pwa-banner-dismissed' }) {
    const [deferred, setDeferred] = useState(null);
    const [visible, setVisible] = useState(false);
    const [iosHint, setIosHint] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        // Don't show if already installed (standalone) or previously dismissed.
        const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches
            || window.navigator.standalone === true;
        const dismissed = localStorage.getItem(storageKey) === '1';
        if (isStandalone || dismissed) return;

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferred(e);
            setVisible(true);
        };
        window.addEventListener('beforeinstallprompt', onPrompt);

        // iOS Safari fallback (no beforeinstallprompt)
        const ua = window.navigator.userAgent || '';
        const isIos = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
        if (isIos) {
            setIosHint(true);
            setVisible(true);
        }

        const onInstalled = () => {
            setVisible(false);
            localStorage.setItem(storageKey, '1');
        };
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onPrompt);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, [storageKey]);

    const dismiss = () => {
        setVisible(false);
        try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    };

    const install = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            await deferred.userChoice;
        } catch { /* ignore */ }
        setDeferred(null);
        setVisible(false);
        try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    };

    if (!visible) return null;

    const title = iosHint ? 'Installer African Valley' : 'Application installable';
    const desc = iosHint
        ? 'Sur iOS : appuyez sur Partager puis « Sur l\'écran d\'accueil ».'
        : 'Ajoutez African Valley à votre écran d\'accueil pour un accès rapide hors ligne — idéal pour le terrain.';

    if (variant === 'inline') {
        return (
            <div
                data-testid="pwa-install-inline"
                className="rounded-xl border border-secondary/30 bg-secondary-light/40 p-5 flex items-start gap-4"
            >
                <div className="shrink-0 h-10 w-10 rounded-lg bg-secondary/15 text-secondary flex items-center justify-center">
                    <Smartphone size={18} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink">{title}</div>
                    <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
                </div>
                <div className="flex items-center gap-2">
                    {!iosHint && (
                        <button
                            type="button"
                            onClick={install}
                            data-testid="pwa-install-btn"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-secondary text-white text-xs font-medium hover:opacity-90"
                        >
                            <Download size={13} /> Installer
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={dismiss}
                        aria-label="Fermer"
                        data-testid="pwa-install-dismiss"
                        className="h-8 w-8 rounded-md text-muted-foreground hover:bg-white"
                    >
                        <X size={14} className="mx-auto" />
                    </button>
                </div>
            </div>
        );
    }

    // floating (Landing)
    return (
        <div
            data-testid="pwa-install-floating"
            className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 sm:max-w-sm rounded-2xl border border-border bg-white shadow-xl p-4 flex items-start gap-3"
            role="dialog"
            aria-live="polite"
        >
            <div className="shrink-0 h-10 w-10 rounded-lg bg-primary-light text-primary flex items-center justify-center">
                <Smartphone size={18} strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-ink text-sm">{title}</div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</p>
                <div className="mt-3 flex items-center gap-2">
                    {!iosHint && (
                        <button
                            type="button"
                            onClick={install}
                            data-testid="pwa-install-btn"
                            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:opacity-90"
                        >
                            <Download size={13} /> Installer
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={dismiss}
                        data-testid="pwa-install-dismiss"
                        className="text-xs text-muted-foreground hover:text-ink"
                    >
                        Plus tard
                    </button>
                </div>
            </div>
            <button
                type="button"
                onClick={dismiss}
                aria-label="Fermer"
                className="shrink-0 h-7 w-7 rounded-md text-muted-foreground hover:bg-surface-muted"
            >
                <X size={14} className="mx-auto" />
            </button>
        </div>
    );
}
